import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, MERCHANT_ROUTES, useAuthStore } from '@dukene/shared';
import { ArrowLeft, Search, Save, PackageSearch, CheckSquare, Square, Tag, Layers } from 'lucide-react';

interface Category {
  id: string;
  name: string;
  description?: string;
  image?: string;
}

interface Product {
  id: string;
  title: string;
  price: number;
  status: string;
  images?: { url: string }[];
  categories: Category[];
}

interface ProductsResponse {
  products: Product[];
  pagination: { total: number; pages: number };
}

export default function CategoryDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const activeStoreId = useAuthStore((state) => state.activeStoreId);

  const [searchQuery, setSearchQuery] = useState('');
  // tracks which products are selected (checked) to be IN this category
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(new Set());
  const [hasModified, setHasModified] = useState(false);
  const [activeTab, setActiveTab] = useState<'uncategorized' | 'in-category'>('uncategorized');

  // ── Fetch category details ────────────────────────────────────────────────
  const { data: category, isLoading: isLoadingCategory } = useQuery<Category>({
    queryKey: ['merchant-category', activeStoreId, id],
    queryFn: () => apiClient.get<Category>(`/products/categories/${id}`),
    enabled: !!id && !!activeStoreId,
  });

  // ── Fetch products already IN this category ───────────────────────────────
  const { data: inCategoryData, isLoading: isLoadingInCategory } = useQuery<ProductsResponse>({
    queryKey: ['merchant-products-in-category', activeStoreId, id],
    queryFn: () =>
      apiClient.get<ProductsResponse>(`/products?categoryId=${id}&status=ALL&limit=5000`),
    enabled: !!id && !!activeStoreId,
  });

  // ── Fetch uncategorised products (no category at all) ────────────────────
  const { data: uncategorizedData, isLoading: isLoadingUncategorized } = useQuery<ProductsResponse>({
    queryKey: ['merchant-products-uncategorized', activeStoreId],
    queryFn: () =>
      apiClient.get<ProductsResponse>(`/products?categoryId=UNCATEGORIZED&status=ALL&limit=5000`),
    enabled: !!activeStoreId,
  });

  const inCategoryProducts = inCategoryData?.products || [];
  const uncategorizedProducts = uncategorizedData?.products || [];

  // ── Seed initial selection from products already in this category ─────────
  useEffect(() => {
    if (inCategoryProducts.length > 0 && id && !hasModified) {
      setSelectedProductIds(new Set(inCategoryProducts.map((p) => p.id)));
    }
  }, [inCategoryProducts, id, hasModified]);

  // ── Products shown in the active tab ─────────────────────────────────────
  // uncategorized tab: products with NO category + products already in THIS category
  // (so you can see and deselect already-assigned ones too)
  const displayProducts = useMemo(() => {
    if (activeTab === 'in-category') return inCategoryProducts;
    // On the uncategorized tab: all uncategorized + already-in-this-category
    // Merge and deduplicate
    const merged = new Map<string, Product>();
    inCategoryProducts.forEach((p) => merged.set(p.id, p));
    uncategorizedProducts.forEach((p) => merged.set(p.id, p));
    return Array.from(merged.values());
  }, [activeTab, inCategoryProducts, uncategorizedProducts]);

  // ── Filter by search ───────────────────────────────────────────────────────
  const filteredProducts = useMemo(() => {
    if (!searchQuery.trim()) return displayProducts;
    const q = searchQuery.toLowerCase();
    return displayProducts.filter((p) => p.title.toLowerCase().includes(q));
  }, [displayProducts, searchQuery]);

  // ── Selection helpers ─────────────────────────────────────────────────────
  const toggleProductSelection = (productId: string) => {
    const next = new Set(selectedProductIds);
    next.has(productId) ? next.delete(productId) : next.add(productId);
    setSelectedProductIds(next);
    setHasModified(true);
  };

  const filteredAllSelected =
    filteredProducts.length > 0 && filteredProducts.every((p) => selectedProductIds.has(p.id));

  const toggleSelectAllFiltered = () => {
    const next = new Set(selectedProductIds);
    if (filteredAllSelected) {
      filteredProducts.forEach((p) => next.delete(p.id));
    } else {
      filteredProducts.forEach((p) => next.add(p.id));
    }
    setSelectedProductIds(next);
    setHasModified(true);
  };

  // ── Save mutation ─────────────────────────────────────────────────────────
  const updateMutation = useMutation({
    mutationFn: (productIds: string[]) =>
      apiClient.put(`/products/categories/${id}/products`, { productIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merchant-products-in-category', activeStoreId, id] });
      queryClient.invalidateQueries({ queryKey: ['merchant-products-uncategorized', activeStoreId] });
      queryClient.invalidateQueries({ queryKey: ['merchant-products', activeStoreId] });
      setHasModified(false);
    },
    onError: (error: any) => {
      alert(error.response?.data?.error?.message || 'Failed to update category products');
    },
  });

  const handleSave = () => updateMutation.mutate(Array.from(selectedProductIds));

  // ── Loading state ─────────────────────────────────────────────────────────
  const isLoading = isLoadingCategory || isLoadingInCategory || isLoadingUncategorized;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900">Category not found</h2>
        <Link to={MERCHANT_ROUTES.CATEGORIES} className="text-blue-600 hover:underline mt-4 inline-block">
          Return to Categories
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6 pb-20">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(MERCHANT_ROUTES.CATEGORIES)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="flex items-center gap-3">
            {category.image ? (
              <img src={category.image} alt={category.name} className="w-12 h-12 rounded-lg object-cover border" />
            ) : (
              <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center border">
                <span className="text-gray-400 font-bold text-xl">{category.name.charAt(0).toUpperCase()}</span>
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{category.name}</h1>
              <p className="text-gray-500 text-sm">
                {inCategoryProducts.length} product{inCategoryProducts.length !== 1 ? 's' : ''} in this category
              </p>
            </div>
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={!hasModified || updateMutation.isPending}
          className="flex items-center gap-2 px-6 py-2.5 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
        >
          {updateMutation.isPending ? (
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
          ) : (
            <Save className="w-5 h-5" />
          )}
          Save Changes
        </button>
      </div>

      {/* Main Panel */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => { setActiveTab('uncategorized'); setSearchQuery(''); }}
            className={`flex items-center gap-2 px-6 py-3.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'uncategorized'
                ? 'border-black text-black'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Tag className="w-4 h-4" />
            Uncategorised
            <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-medium ${
              activeTab === 'uncategorized' ? 'bg-black text-white' : 'bg-gray-100 text-gray-600'
            }`}>
              {uncategorizedProducts.length + inCategoryProducts.length}
            </span>
          </button>
          <button
            onClick={() => { setActiveTab('in-category'); setSearchQuery(''); }}
            className={`flex items-center gap-2 px-6 py-3.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'in-category'
                ? 'border-black text-black'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Layers className="w-4 h-4" />
            In This Category
            <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-medium ${
              activeTab === 'in-category' ? 'bg-black text-white' : 'bg-gray-100 text-gray-600'
            }`}>
              {selectedProductIds.size}
            </span>
          </button>
        </div>

        {/* Tab description */}
        <div className="px-4 pt-3 pb-0">
          {activeTab === 'uncategorized' ? (
            <p className="text-xs text-gray-500">
              Products not yet assigned to any category, plus products already in <strong>{category.name}</strong>. Check/uncheck to assign or remove.
            </p>
          ) : (
            <p className="text-xs text-gray-500">
              All products currently assigned to <strong>{category.name}</strong>. Uncheck to remove from this category.
            </p>
          )}
        </div>

        {/* Toolbar */}
        <div className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent outline-none transition-all"
            />
          </div>

          <div className="flex items-center gap-4 text-sm">
            <span className="text-gray-600 font-medium bg-gray-100 px-3 py-1 rounded-full">
              {selectedProductIds.size} selected
            </span>
            <button
              onClick={toggleSelectAllFiltered}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium text-gray-700"
            >
              {filteredAllSelected ? <Square className="w-4 h-4" /> : <CheckSquare className="w-4 h-4" />}
              {filteredAllSelected ? 'Deselect All' : 'Select All'}
            </button>
          </div>
        </div>

        {/* Product Grid */}
        <div className="px-4 pb-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 max-h-[600px] overflow-y-auto bg-gray-50/50">
          {filteredProducts.length === 0 ? (
            <div className="col-span-full p-12 text-center text-gray-500">
              <PackageSearch className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              {searchQuery ? (
                <p>No products match your search.</p>
              ) : activeTab === 'in-category' ? (
                <div>
                  <p className="font-medium">No products in this category yet.</p>
                  <p className="text-sm mt-1">Switch to the <span className="font-medium">Uncategorised</span> tab to add products.</p>
                </div>
              ) : (
                <div>
                  <p className="font-medium">All products are already categorised.</p>
                  <p className="text-sm mt-1">Every product in your store belongs to at least one category.</p>
                </div>
              )}
            </div>
          ) : (
            filteredProducts.map((product) => {
              const isSelected = selectedProductIds.has(product.id);
              const isAlreadyInThisCategory = inCategoryProducts.some((p) => p.id === product.id);

              return (
                <div
                  key={product.id}
                  onClick={() => toggleProductSelection(product.id)}
                  className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all duration-200 ${
                    isSelected
                      ? 'bg-blue-50 border-blue-300 shadow-sm'
                      : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'
                  }`}
                >
                  {/* Checkbox */}
                  <div className="flex-shrink-0">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                      isSelected ? 'bg-black border-black text-white' : 'border-gray-300 bg-white'
                    }`}>
                      {isSelected && <CheckSquare className="w-3 h-3" />}
                    </div>
                  </div>

                  {/* Product thumbnail */}
                  {product.images?.[0]?.url && (
                    <img
                      src={product.images[0].url}
                      alt={product.title}
                      className="w-8 h-8 rounded-md object-cover flex-shrink-0 border border-gray-200"
                    />
                  )}

                  {/* Name + badge */}
                  <div className="flex-1 min-w-0">
                    <h3
                      className={`text-sm font-medium truncate ${isSelected ? 'text-blue-900' : 'text-gray-700'}`}
                      title={product.title}
                    >
                      {product.title}
                    </h3>
                    {isAlreadyInThisCategory && activeTab === 'uncategorized' && (
                      <span className="text-[10px] font-medium text-blue-600 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded-full">
                        In category
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
